import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getOrganizationSettingsApi } from '../api/api';
import { useAuth } from './AuthContext';

const OrganizationSettingsContext = createContext(null);

export function useOrganizationSettings() {
  const context = useContext(OrganizationSettingsContext);
  if (!context) {
    throw new Error('useOrganizationSettings must be used within OrganizationSettingsProvider');
  }
  return context;
}

// Helper to get companyId from localStorage (avoid circular dependency with CompanyContext)
const getCompanyIdFromStorage = () => {
  try {
    const stored = localStorage.getItem('selectedCompany');
    if (stored) {
      const company = JSON.parse(stored);
      return company?.value || null;
    }
  } catch (e) {
    // Ignore parse errors
  }
  return null;
};

export default function OrganizationSettingsProvider({ children }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    billWise: false,
    userWise: false,
    beatWise: false,
    loading: true
  });
  const companyIdRef = useRef(null);

  const fetchSettings = async () => {
    try {
      setSettings(prev => ({ ...prev, loading: true }));
      // Get companyId from localStorage to avoid circular dependency
      const companyId = getCompanyIdFromStorage();
      companyIdRef.current = companyId;
      // Pass companyId as query parameter if available
      const params = companyId ? { companyId } : {};
      const res = await getOrganizationSettingsApi(params);
      const data = res.data?.data || res.data || {};
      setSettings({
        billWise: data.billWise || false,
        userWise: data.userWise || false,
        beatWise: data.beatWise || false,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching organization settings:', error);
      // Default to false if error
      setSettings({
        billWise: false,
        userWise: false,
        beatWise: false,
        loading: false
      });
    }
  };

  useEffect(() => {
    if (user) {
      fetchSettings();
      
      // Listen for custom event for same-tab updates when company changes
      const handleCompanyChange = () => {
        const newCompanyId = getCompanyIdFromStorage();
        // Only refetch if companyId actually changed
        if (newCompanyId !== companyIdRef.current) {
          fetchSettings();
        }
      };
      
      window.addEventListener('companyChanged', handleCompanyChange);
      
      // Also poll localStorage periodically to catch changes (fallback)
      const intervalId = setInterval(() => {
        const currentCompanyId = getCompanyIdFromStorage();
        if (currentCompanyId !== companyIdRef.current) {
          fetchSettings();
        }
      }, 1000); // Check every second
      
      return () => {
        window.removeEventListener('companyChanged', handleCompanyChange);
        clearInterval(intervalId);
      };
    } else {
      setSettings(prev => ({ ...prev, loading: false }));
    }
  }, [user]);

  return (
    <OrganizationSettingsContext.Provider value={{ settings, fetchSettings }}>
      {children}
    </OrganizationSettingsContext.Provider>
  );
}

